import React, { useCallback, useEffect, useState } from 'react';
import Flatpickr from 'react-flatpickr';
import dayjs, { Dayjs } from 'dayjs';
import { Helmet } from 'react-helmet';
import NewOrder from 'pages/new-order/NewOrder';
import { PageUiNavBar } from 'components/Layout/PageNavBar/PageUiNavBar';
import { RenderOrderGroup } from './RenderOrderGroup';
import OrdersMenuBar from './components/OrdersMenuBar';
import './Orders.scss';
import {
  AddressValidity,
  OrderDeliveryType,
  OrderListOrderClazz,
  OrderPackageSize,
  OrderSearchStatus,
  Region,
} from '../../types/orderTypes';
import Button from '../../components/buttons/Button';
import { fetchOrdersForOrderListAsync } from '../../fetch/orderFetch';
import { Loader } from '../../components/Loader/Loader';
import * as H from 'history';
import { getParametersFromQueryString } from '../../utils/urlUtil';
import { getHubConfig } from '../../const/HubConfig';
import ChangeToHubMultipleModal from '../../components/modals/ChangeToHubMultipleModal';
import SelectDeliveryDateMultipleModal from '../../components/modals/SelectDeliveryDateMultipleModal';
import SendMessageModal from '../../components/modals/SendMessageModal';
import { AddressFix } from './AddressFix/AddressFix';
import { BatchActions } from './batchActions/BatchActions';

export type FilterCounts = {
  totalCount: number;
  partnerScanCount: number;
  hubScanCount: number;
  largeProductCount: number;
};

export enum OrderSort {
  Delivery = 'delivery',
  Created = 'created',
}

export function defaultSortOrderForStatus(
  status: OrderSearchStatus
): OrderSort {
  if (status === OrderSearchStatus.Active) {
    return OrderSort.Delivery;
  } else {
    return OrderSort.Created;
  }
}

function endDateForStatus(status: OrderSearchStatus): Dayjs {
  if (status === OrderSearchStatus.Delivered) {
    return dayjs();
  } else if (status === OrderSearchStatus.Cancelled) {
    return dayjs();
  } else if (status === OrderSearchStatus.Active) {
    return dayjs().add(7, 'day');
  }
}

function startDateForStatus(status: OrderSearchStatus): Dayjs {
  if (status === OrderSearchStatus.Delivered) {
    return dayjs().add(-3, 'day');
  } else if (status === OrderSearchStatus.Cancelled) {
    return dayjs().add(-3, 'day');
  } else if (status === OrderSearchStatus.Active) {
    return dayjs().add(-7, 'day');
  }
}

function filterOrders(
  orders: OrderListOrderClazz[],
  searchFilter: string,
  onlyScannedAtPartner: boolean,
  onlyUnscannedAtPartner: boolean,
  onlyUnscannedAtHub: boolean,
  onlyLargeProduct: boolean,
  onlyScannedAtHub: boolean
): OrderListOrderClazz[] {
  let filteredOrders = [...orders];

  filteredOrders = onlyScannedAtPartner
    ? filteredOrders.filter(order => order.scannedAtPartner)
    : filteredOrders;

  filteredOrders = onlyUnscannedAtPartner
    ? filteredOrders.filter(order => !order.scannedAtPartner)
    : filteredOrders;

  filteredOrders = onlyUnscannedAtHub
    ? filteredOrders.filter(order => !order.scannedAtHub)
    : filteredOrders;

  filteredOrders = onlyLargeProduct
    ? filteredOrders.filter(order => order.product === 'large')
    : filteredOrders;

  filteredOrders = onlyScannedAtHub
    ? filteredOrders.filter(order => order.scannedAtHub)
    : filteredOrders;

  const searchText = searchFilter.trim().toLowerCase();
  const sizeSearch =
    searchText === 'xl'
      ? OrderPackageSize.Xxl
      : ['small', 'medium', 'large', 'xxl'].includes(searchText)
      ? (searchText.toUpperCase() as OrderPackageSize)
      : undefined;

  if (sizeSearch) {
    filteredOrders = filteredOrders.filter(
      order => order.packageSize === sizeSearch
    );
  } else if (searchText !== '') {
    const matches = (text?: string) =>
      text ? text.toLowerCase().includes(searchText) : false;
    filteredOrders = filteredOrders.filter(
      order =>
        order.id.includes(searchText) ||
        matches(order.originAddress.fullAddressString) ||
        matches(order.pickupName) ||
        matches(order.pickupPhone) ||
        matches(order.pickupEmail) ||
        matches(order.destinationAddress.fullAddressString) ||
        matches(order.recipientName) ||
        matches(order.recipientPhone) ||
        matches(order.recipientEmail) ||
        matches(order.senderName) ||
        matches(order.senderEmail) ||
        matches(order.orderReference)
    );
  }
  return filteredOrders;
}

function sortOrders(
  orders: OrderListOrderClazz[],
  orderBy: OrderSort
): OrderListOrderClazz[] {
  if (orderBy === OrderSort.Delivery) {
    const sortedOrdersByDelivery = orders.sort((a, b) => {
      const deliverComp = a.deliverFromDate.diff(b.deliverFromDate);
      return deliverComp !== 0
        ? deliverComp
        : a.createdDateDate.diff(b.createdDateDate);
    });
    return sortedOrdersByDelivery;
  } else if (orderBy === OrderSort.Created) {
    const sortedOrdersByCreated = orders.sort((a, b) =>
      a.createdDateDate.diff(b.createdDateDate)
    );
    return sortedOrdersByCreated;
  }
}

export function searchFilterOrders(
  currentSearch: string,
  sortOrdersBy: OrderSort,
  onlyScannedAtPartner: boolean,
  onlyUnscannedAtPartner: boolean,
  onlyUnscannedAtHub: boolean,
  onlyLargeProduct: boolean,
  onlyScannedAtHub: boolean,
  orders: OrderListOrderClazz[]
): OrderListOrderClazz[] {
  const ordersToSort: OrderListOrderClazz[] = filterOrders(
    orders,
    currentSearch,
    onlyScannedAtPartner,
    onlyUnscannedAtPartner,
    onlyUnscannedAtHub,
    onlyLargeProduct,
    onlyScannedAtHub
  );
  return sortOrders(ordersToSort, sortOrdersBy);
}

declare interface OrdersProps {
  region: Region;
  location: H.Location;
}

export default function Orders({ region, location }: OrdersProps) {
  const [toggleOrderMode, setToggleOrderMode] = useState('Undelivered');

  const search = getParametersFromQueryString(location.search)['q'] || '';

  return (
    <>
      <Helmet>
        <title>Orders - {toggleOrderMode}</title>
      </Helmet>
      <PageUiNavBar
        bars={[
          {
            text: `Undelivered`,
            method: () => setToggleOrderMode('Undelivered'),
            active: toggleOrderMode === 'Undelivered',
          },
          {
            text: `Delivered`,
            method: () => setToggleOrderMode('Delivered'),
            active: toggleOrderMode === 'Delivered',
          },
          {
            text: `Canceled`,
            method: () => setToggleOrderMode('Canceled'),
            active: toggleOrderMode === 'Canceled',
          },
          {
            text: `New Order`,
            method: () => setToggleOrderMode('New Order'),
            active: toggleOrderMode === 'New Order',
          },
          {
            text: `Address Fix`,
            method: () => setToggleOrderMode('Address Fix'),
            active: toggleOrderMode === 'Address Fix',
          },
          {
            text: `Batch Actions`,
            method: () => setToggleOrderMode('Batch Actions'),
            active: toggleOrderMode === 'Batch Actions',
          },
        ]}
      />
      <>
        {toggleOrderMode === 'Undelivered' && (
          <ShowOrders
            orderSection={'Undelivered orders'}
            orderStatus={OrderSearchStatus.Active}
            region={region}
            searchQuery={search}
          />
        )}
        {toggleOrderMode === 'Delivered' && (
          <ShowOrders
            orderSection={'Delivered orders'}
            orderStatus={OrderSearchStatus.Delivered}
            region={region}
            searchQuery={search}
          />
        )}
        {toggleOrderMode === 'Canceled' && (
          <ShowOrders
            orderSection={'Canceled orders'}
            orderStatus={OrderSearchStatus.Cancelled}
            region={region}
            searchQuery={search}
          />
        )}
        {toggleOrderMode === 'New Order' && <NewOrder />}

        {toggleOrderMode === 'Address Fix' && <AddressFix region={region} />}

        {toggleOrderMode === 'Batch Actions' && <BatchActions />}
      </>
    </>
  );
}

export interface OrderDisplayGroup {
  title: string;
  orders: OrderListOrderClazz[];
}

export function createOrderDisplayGroups(
  orders: OrderListOrderClazz[]
): OrderDisplayGroup[] {
  const now = dayjs();
  const remainingToday = orders.filter(
    order => order.isToday(now) && order.isAfterNow(now)
  );

  return [
    {
      title: 'Todays problem orders',
      orders: remainingToday
        .filter(order => order.deliveryType === OrderDeliveryType.Scheduled)
        .filter(
          order =>
            order.destinationAddress.addressValidity ===
            AddressValidity.NEEDS_MANUAL_VALIDATION
        ),
    },
    {
      title: 'Outdated orders',
      orders: orders.filter(order => order.isBeforeNow(now)),
    },
    {
      title: 'Express Orders Today',
      orders: remainingToday.filter(
        order => order.deliveryType === OrderDeliveryType.Express
      ),
    },
    {
      title: 'Todays orders',
      orders: remainingToday
        .filter(order => order.deliveryType === OrderDeliveryType.Scheduled)
        .filter(
          order =>
            order.destinationAddress.addressValidity !==
            AddressValidity.NEEDS_MANUAL_VALIDATION
        ),
    },
    {
      title: 'Tomorrows orders',
      orders: orders.filter(order => order.isTomorrow(now)),
    },
    {
      title: 'Future orders',
      orders: orders.filter(order => order.isAfterTomorrow(now)),
    },
  ];
}

export function ShowOrders({
  orderSection,
  orderStatus,
  region,
  searchQuery,
}: {
  orderSection: string;
  orderStatus: OrderSearchStatus;
  region: Region;
  searchQuery: string;
}) {
  const [fetchedOrders, setFetchedOrders] = useState<OrderListOrderClazz[]>();
  const [sortOrdersBy, setSortOrdersBy] = useState<OrderSort>(
    defaultSortOrderForStatus(orderStatus)
  );
  const [currentSearch, setCurrentSearch] = useState(searchQuery);
  const [dateRange, setDateRange] = useState({
    startDate: startDateForStatus(orderStatus),
    endDate: endDateForStatus(orderStatus),
  });
  const [onlyScannedAtPartner, setOnlyScannedAtPartner] = useState(false);
  const [onlyUnscannedAtPartner, setOnlyUnscannedAtPartner] = useState(false);
  const [onlyUnscannedAtHub, setOnlyUnscannedAtHub] = useState(false);
  const [onlyLargeProduct, setOnlyLargeProduct] = useState(false);
  const [onlyScannedAtHub, setOnlyScannedAtHub] = useState(false);
  const [selectedOrderIds, setSelectedOrderIds] = useState<{
    [orderId: string]: boolean;
  }>({});
  const [showChangeToHubModal, setShowChangeToHubModal] =
    useState<boolean>(false);

  const onSelectOrdersClick = (orderIds: { [orderId: string]: boolean }) =>
    setSelectedOrderIds(prevState => ({
      ...prevState,
      ...orderIds,
    }));

  const [
    showSelectDeliveryDateMultipleModal,
    setShowSelectDeliveryDateMultipleModal,
  ] = useState(false);
  const [showSendMessageModal, setShowSendMessageModal] = useState(false);

  useEffect(() => {
    fetchOrdersForOrderListAsync(
      dateRange.startDate,
      dateRange.endDate,
      orderStatus,
      true,
      region
    ).then(orders => setFetchedOrders(orders));
  }, [dateRange, orderStatus, region]);

  const [filterCounts, setFilterCounts] = useState<FilterCounts>({
    totalCount: 0,
    partnerScanCount: 0,
    hubScanCount: 0,
    largeProductCount: 0,
  });

  useEffect(() => {
    if (fetchedOrders) {
      const counts = {
        totalCount: fetchedOrders.length,
        partnerScanCount: 0,
        hubScanCount: 0,
        largeProductCount: 0,
      };
      fetchedOrders.forEach(order => {
        order.scannedAtPartner && counts.partnerScanCount++;
        order.scannedAtHub && counts.hubScanCount++;
        order.product === 'large' && counts.largeProductCount++;
      });

      setFilterCounts(counts);
    }
  }, [fetchedOrders]);

  let showOrders: OrderDisplayGroup[] = undefined;
  if (fetchedOrders) {
    const searchAndFiltered = searchFilterOrders(
      currentSearch,
      sortOrdersBy,
      onlyScannedAtPartner,
      onlyUnscannedAtPartner,
      onlyUnscannedAtHub,
      onlyLargeProduct,
      onlyScannedAtHub,
      fetchedOrders
    );

    if (orderStatus === OrderSearchStatus.Active) {
      showOrders = createOrderDisplayGroups(searchAndFiltered);
    } else {
      showOrders = [{ title: orderSection, orders: searchAndFiltered }];
    }
  }

  function ChooseDate() {
    const numberOfMonth = () => {
      return window.matchMedia('(max-width: 700px').matches ? 1 : 2;
    };

    const [numberOfMonthShown, setNumberOfMonthShown] = useState(
      numberOfMonth()
    );

    const handleWindowResize = useCallback(event => {
      const numberMonths = window.innerWidth > 700 ? 2 : 1;
      setNumberOfMonthShown(numberMonths);
    }, []);

    useEffect(() => {
      window.addEventListener('resize', handleWindowResize);
      return () => {
        window.removeEventListener('resize', handleWindowResize);
      };
    }, [handleWindowResize]);

    const doOnClose = selectedDates => {
      if (selectedDates != null && selectedDates.length > 0) {
        const secondIndex = selectedDates.length === 2 ? 1 : 0;
        setDateRange({
          startDate: dayjs(selectedDates[0]).clone(),
          endDate: dayjs(selectedDates[secondIndex]).clone(),
        });
      }
    };

    const doCheckSelectedPeriod = (selectedDates, instance) => {
      if (selectedDates != null && selectedDates.length === 1) {
        const startDate = dayjs(selectedDates[0]);
        const maxDate = startDate.add(14, 'day');
        const minDate = startDate.subtract(14, 'day');
        instance.set('minDate', minDate.toISOString());
        instance.set('maxDate', maxDate.toISOString());
        instance.jumpToDate(minDate.toISOString(), true);
      }
    };

    const defaultRange = () => {
      return [dateRange.startDate.toDate(), dateRange.endDate.toDate()];
    };

    const checkPeriod = () => {
      return dateRange.startDate.isBefore(
        dateRange.endDate.clone().subtract(13, 'day'),
        'day'
      );
    };

    const modifyDateRange = (startOrEnd, plusOrMinus) => {
      const delta = plusOrMinus === 'plus' ? 1 : -1;
      if (startOrEnd === 'start') {
        const start = dayjs(dateRange.startDate.clone().add(delta, 'day'));
        const end = dateRange.endDate.clone();
        setDateRange({ startDate: start, endDate: end });
      } else {
        const end = dayjs(dateRange.endDate.clone().add(delta, 'day'));
        const start = dateRange.startDate.clone();
        setDateRange({ startDate: start, endDate: end });
      }
    };

    const inputStyle = {
      fontSize: '1.5rem',
      paddingTop: '8px',
      paddingBottom: '8px',
      width: '300px',
      height: '40px',
    };

    return (
      <>
        <div className={'dateMenu'}>
          <Flatpickr
            onChange={(selectedDates, representation, instance) =>
              doCheckSelectedPeriod(selectedDates, instance)
            }
            onClose={selectedDates => doOnClose(selectedDates)}
            className=""
            options={{
              dateFormat: 'l d M',
              defaultDate: defaultRange(),
              mode: 'range',
              showMonths: numberOfMonthShown,
              shorthandCurrentMonth: true,
              locale: { firstDayOfWeek: 1 },
            }}
            render={(props, ref) => (
              <div className="insideFlatPickr">
                <div
                  style={{
                    display: 'flex',
                    flexDirection: 'row',
                    height: '40px',
                  }}
                >
                  <Button
                    title="<"
                    onClick={() => modifyDateRange('start', 'minus')}
                    disabled={checkPeriod()}
                    style={{ maxWidth: '40px' }}
                  />
                  <Button
                    title=">"
                    onClick={() => modifyDateRange('start', 'plus')}
                    disabled={dateRange.startDate.isSame(dateRange.endDate)}
                    style={{ maxWidth: '40px' }}
                  />
                </div>
                <input
                  style={inputStyle}
                  className={'form-control'}
                  type="text"
                  ref={ref}
                />
                <div
                  style={{
                    display: 'flex',
                    flexDirection: 'row',
                    height: '40px',
                  }}
                >
                  <Button
                    title="<"
                    onClick={() => modifyDateRange('end', 'minus')}
                    disabled={dateRange.startDate.isSame(dateRange.endDate)}
                    style={{ maxWidth: '40px' }}
                  />
                  <Button
                    title=">"
                    onClick={() => modifyDateRange('end', 'plus')}
                    disabled={checkPeriod()}
                    style={{ maxWidth: '40px' }}
                  />
                </div>
              </div>
            )}
          />
        </div>
      </>
    );
  }

  const searchFilter = currentSearch.trim() !== '';
  const selectedOrdersCount = Object.keys(selectedOrderIds).filter(
    orderId => selectedOrderIds[orderId]
  ).length;
  const changeToHubOrders = (fetchedOrders || []).filter(
    order => !!selectedOrderIds[order.id]
  );

  const CHANGE_ADDRESS = 'Change Address To Hub...';
  const SET_NEW_DATE = 'Set New Delivery Date...';
  const SEND_MESSAGE = 'Send Message...';

  const allowChangeToHubOrMoveDate = orderStatus === OrderSearchStatus.Active;
  const menuBarButtonText = (text: string, amount?: number) =>
    amount === 0 ? text : `${text} (${amount})`;

  return (
    <div className="orders">
      <div className="menuBar">
        <ChooseDate />
        <OrdersMenuBar
          onFilterChange={filter => setCurrentSearch(filter)}
          currentSearch={currentSearch}
          currentSort={sortOrdersBy}
          changeSortBy={status => setSortOrdersBy(status)}
          onScannedAtPartnerFilterChange={() => {
            if (!onlyScannedAtPartner) {
              setOnlyUnscannedAtPartner(false);
            }
            setOnlyScannedAtPartner(!onlyScannedAtPartner);
          }}
          onUnscannedAtPartnerFilterChange={() => {
            if (!onlyUnscannedAtPartner) {
              setOnlyScannedAtPartner(false);
            }
            setOnlyUnscannedAtPartner(!onlyUnscannedAtPartner);
          }}
          onUnscannedAtHubFilterChange={() => {
            if (!onlyUnscannedAtHub) {
              setOnlyScannedAtHub(false);
            }
            setOnlyUnscannedAtHub(!onlyUnscannedAtHub);
          }}
          onLargeProductFilterChange={() =>
            setOnlyLargeProduct(!onlyLargeProduct)
          }
          largeProductFilter={onlyLargeProduct}
          onScannedAtHubFilterChange={() => {
            if (!onlyScannedAtHub) {
              setOnlyUnscannedAtHub(false);
            }
            setOnlyScannedAtHub(!onlyScannedAtHub);
          }}
          onlyScannedAtPartner={onlyScannedAtPartner}
          onlyUnscannedAtPartner={onlyUnscannedAtPartner}
          onlyUnscannedAtHub={onlyUnscannedAtHub}
          onlyScannedAtHub={onlyScannedAtHub}
          labelId={'orders-page'}
          filterCounts={filterCounts}
        />
      </div>
      <div className="menuBarSecondRow">
        {allowChangeToHubOrMoveDate && (
          <>
            <div style={{ marginRight: '2px', marginBottom: '5px' }}>
              <Button
                title={menuBarButtonText(CHANGE_ADDRESS, selectedOrdersCount)}
                onClick={() => setShowChangeToHubModal(true)}
                disabled={selectedOrdersCount === 0}
              />
            </div>
            <div style={{ marginRight: '2px', marginBottom: '5px' }}>
              <Button
                title={menuBarButtonText(SET_NEW_DATE, selectedOrdersCount)}
                onClick={() => setShowSelectDeliveryDateMultipleModal(true)}
                disabled={selectedOrdersCount === 0}
              />
            </div>
            <div style={{ marginRight: '2px', marginBottom: '5px' }}>
              <Button
                title={menuBarButtonText(SEND_MESSAGE, selectedOrdersCount)}
                onClick={() => setShowSendMessageModal(true)}
                disabled={selectedOrdersCount === 0}
              />
            </div>
          </>
        )}
      </div>
      {!showOrders && <Loader />}

      {showOrders && (
        <div>
          <div>
            {searchFilter && <div>Showing results based on search</div>}

            {showOrders.map(displayGroup => {
              return (
                displayGroup.orders.length > 0 && (
                  <RenderOrderGroup
                    key={displayGroup.title}
                    title={displayGroup.title}
                    orders={displayGroup.orders}
                    totalNumber={fetchedOrders.length}
                    selectedOrders={selectedOrderIds}
                    onOrderSelected={
                      allowChangeToHubOrMoveDate &&
                      ((orderId: string) =>
                        setSelectedOrderIds(prevState => ({
                          ...prevState,
                          [orderId]: !prevState[orderId],
                        })))
                    }
                    region={region}
                    onSelectOrderClick={onSelectOrdersClick}
                  />
                )
              );
            })}
          </div>
        </div>
      )}
      {allowChangeToHubOrMoveDate && showChangeToHubModal && (
        <ChangeToHubMultipleModal
          orderIds={changeToHubOrders.map(order => order.id)}
          hubAddress={getHubConfig(region).hubAddress}
          onCloseModal={() => setShowChangeToHubModal(false)}
          onSelectModal={() => setShowChangeToHubModal(false)}
        />
      )}
      {allowChangeToHubOrMoveDate && showSelectDeliveryDateMultipleModal && (
        <SelectDeliveryDateMultipleModal
          orders={changeToHubOrders}
          triggerReload={true}
          onCloseModal={() => setShowSelectDeliveryDateMultipleModal(false)}
        />
      )}
      {allowChangeToHubOrMoveDate && showSendMessageModal && (
        <SendMessageModal
          orders={changeToHubOrders}
          onCloseModal={() => setShowSendMessageModal(false)}
        />
      )}
    </div>
  );
}
